import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { FUTURE_MONEY_GIFT_URL, FUTURE_MONEY_URL } from "@constants";
import { Routes } from "@ctypes";
import { useReferralData, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { ArtPanel, Eyebrow, Section, SerifTitle, ThreeUp } from "./styles";
import { home } from "./tokens";

/**
 * Marketing cards carry their own painterly gradient grounds (from the
 * spec) with card-local grain, on top of the shared page mesh.
 */
const PaintedCard = styled(ArtPanel)<{ $bg: string }>`
  display: flex;
  flex-direction: column;
  min-height: 330px;
  padding: 28px;
  gap: 14px;
  background: ${({ $bg }) => $bg};
`;

const IconBadge = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: rgba(14, 18, 26, 0.34);
  border: 1px solid ${home.line14};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardTitle = styled.div`
  font-family: ${home.serif};
  font-size: 23px;
  line-height: 1.22;
  letter-spacing: -0.2px;
  text-wrap: pretty;
`;

const CardBody = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: ${home.ink66};
`;

const CardCta = styled.button`
  height: 44px;
  align-self: flex-start;
  display: flex;
  align-items: center;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid ${home.line26};
  background: rgba(14, 18, 26, 0.24);
  color: ${home.ink};
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 500;
  margin-top: auto;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }
`;

const LEARNING_BG = `radial-gradient(460px 340px at 8% 4%, rgba(143,191,176,0.52), transparent 68%),
    radial-gradient(420px 380px at 96% 104%, rgba(224,155,124,0.30), transparent 66%),
    radial-gradient(300px 300px at 78% 18%, rgba(78,102,124,0.34), transparent 70%),
    linear-gradient(158deg, #1B2731 0%, #10141C 100%)`;

const GIFT_BG = `radial-gradient(460px 340px at 92% 2%, rgba(224,155,124,0.58), transparent 66%),
    radial-gradient(440px 400px at 2% 102%, rgba(143,191,176,0.26), transparent 66%),
    radial-gradient(320px 300px at 44% 46%, rgba(176,104,84,0.32), transparent 70%),
    linear-gradient(158deg, #2A2129 0%, #130F16 100%)`;

const REFERRAL_BG = `radial-gradient(470px 350px at 52% -6%, rgba(233,164,186,0.52), transparent 66%),
    radial-gradient(450px 390px at 98% 94%, rgba(232,163,133,0.40), transparent 66%),
    radial-gradient(360px 340px at 2% 72%, rgba(126,112,168,0.30), transparent 70%),
    radial-gradient(300px 260px at 74% 30%, rgba(224,155,124,0.20), transparent 70%),
    linear-gradient(158deg, #2A2130 0%, #12111A 100%)`;

export const DoMoreSection = () => {
  const navigate = useNavigate();
  const { hasReferralAccess } = useUser();
  const { referralAmount } = useReferralData();

  return (
    <Section style={{ paddingBottom: 0 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Eyebrow>{i18n.t(Translations.HomeDoMoreEyebrow)}</Eyebrow>
        <SerifTitle>
          {i18n.t(Translations.HomeDoMoreTitleLead)}{" "}
          <em>{i18n.t(Translations.HomeDoMoreTitleHighlight)}</em>.
        </SerifTitle>
      </div>

      <ThreeUp>
        <PaintedCard $bg={LEARNING_BG}>
          <IconBadge>
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#C7E6DA"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 4 2 9l10 5 10-5-10-5Z" />
              <path d="M6 11.5V17c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-5.5" />
              <path d="M21 9.5V15" />
            </svg>
          </IconBadge>
          <CardTitle>{i18n.t(Translations.HomeLearningTitle)}</CardTitle>
          <CardBody>{i18n.t(Translations.HomeLearningBody)}</CardBody>
          <CardCta onClick={() => window.open(FUTURE_MONEY_URL, "_blank")}>
            {i18n.t(Translations.HomeLearningCTA)}
          </CardCta>
        </PaintedCard>

        <PaintedCard $bg={GIFT_BG}>
          <IconBadge>
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#F3CDB8"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="9" width="18" height="12" rx="1.5" />
              <path d="M3 13h18" />
              <path d="M12 9v12" />
              <path d="M12 9C10 9 7.5 8.6 7.5 6.5A2.5 2.5 0 0 1 12 5a2.5 2.5 0 0 1 4.5 1.5C16.5 8.6 14 9 12 9Z" />
            </svg>
          </IconBadge>
          <CardTitle>{i18n.t(Translations.HomeGiftTitle)}</CardTitle>
          <CardBody>{i18n.t(Translations.HomeGiftBody)}</CardBody>
          <CardCta onClick={() => window.open(FUTURE_MONEY_GIFT_URL, "_blank", "noopener")}>
            {i18n.t(Translations.HomeGiftCTA)}
          </CardCta>
        </PaintedCard>

        {hasReferralAccess && (
          <PaintedCard $bg={REFERRAL_BG}>
            <IconBadge>
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F4CEDA"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="8.5" cy="8" r="3" />
                <path d="M2.5 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
                <circle cx="17.5" cy="10" r="2.4" />
                <path d="M16 15.6c2.9.3 5 2.2 5 4.4" />
              </svg>
            </IconBadge>
            <CardTitle>
              {i18n.t(Translations.HomeReferralTitle, {
                amount: referralAmount,
              })}
            </CardTitle>
            <CardBody>
              {i18n.t(Translations.HomeReferralBody, {
                amount: referralAmount,
              })}
            </CardBody>
            <CardCta onClick={() => navigate(Routes.ReferralDashboard)}>
              {i18n.t(Translations.HomeReferralCTA)}
            </CardCta>
          </PaintedCard>
        )}
      </ThreeUp>
    </Section>
  );
};
